import { useState } from 'react'
import { get, isEmpty } from "lodash"

type RecordObject = Record<string, any>

type FormObject = {
  [key: string]: string | boolean | RecordObject;
}

type Validations = {
  required?: boolean;
  [key: string]: any;
}

export default function useForm(initialValues: RecordObject = {}) {
  const [values, setValues] = useState<RecordObject>({ ...initialValues })
  const [errors, setErrors] = useState<FormObject>({})
  const [touched, setTouched] = useState<FormObject>({})
  const [fieldValidations, setFieldValidations] = useState<Record<string, Validations>>({})
  const [dirty, setDirty] = useState<boolean>(false)
  
  const form = {
    errors,
    touched,
    values,
    dirty,
    resetDirty: () => setDirty(false)
  }

  function resetForm() {
    setValues(initialValues)
  }

  function setFieldValue(name: string, newValue: any) {
    validateField(name, newValue)
    setValues(existing => Object.assign(existing, { [name]: newValue }))
  }

  function validateForm() {
    const fieldNames = Object.keys(fieldValidations)
    const newTouched = fieldNames.reduce((acc, name) => {
      return { ...acc, [name]: true }
    }, {})
    setTouched(newTouched)
    const newErrors = fieldNames.reduce((acc, name) => {
      const error = checkFieldError(name) || {}
      return { ...acc, ...error }
    }, {})
    setErrors(newErrors)
    return isEmpty(newErrors)
  }

  function checkFieldError(name: string, value = null) {
    const validations = get(fieldValidations, name)
    const fieldValue = value || get(values, name)
    if (validations?.required && !fieldValue) {
      return { [name]: "Required" }
    } else if (validations?.email && !/.*@.*\..*/.test(fieldValue)) {
      return { [name]: "Invalid email" }
    }
  }

  function validateField(name: string, value = null) {
    const fieldValue = value || get(values, name)
    const newError = checkFieldError(name, fieldValue)
    if (newError) {
      setErrors(prevErrors => ({ ...prevErrors, ...newError }))
    } else {
      const { [name]: _, ...newErrors } = errors
      setErrors(newErrors)
    }
  }

  function registerField(name: string, validations: Validations = {}) {
    if (!get(fieldValidations, name)) {
      setFieldValidations(prevValidations => ({ ...prevValidations, [name]: validations }))
    }

    function setValue(eventOrValue: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | string) {
      const newValue = typeof eventOrValue === 'object' ? eventOrValue.target.value : eventOrValue
      setDirty(true)
      setFieldValue(name, newValue)
    }
    
    const setFieldTouched = () => {
      validateField(name)
      if (!get(touched, name)) setTouched({ ...touched, [name]: true })
    }

    return {
      name,
      onBlur: setFieldTouched,
      onChange: setValue,
      required: validations.required,
      value: values[name] || "",
      form,
    }
  }

  return { form, registerField, validateForm, resetForm }
}
